import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Tile, Wizard, WizardStep, Checkbox } from '@dsi/react-eds';
import { PipelineItem, PipelineStatus } from '../../const/PipelineTypes';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import { useTheme } from '../../hooks/useTheme';
import { rappConfigService } from '../../services/rappConfigService';
import { AxiosError } from 'axios';
import LineNumberedEditor from './LineNumberedEditor';

interface RappConfigDialogProps {
  open: boolean;
  onClose: () => void;
  pipeline: PipelineItem;
}

function getManualConfigStep(pipeline: PipelineItem) {
  return pipeline.steps
    .filter(
      (s) =>
        s.Status === PipelineStatus.Manual &&
        s.Stage.toLowerCase().includes('artifacts deployment') &&
        s.Step.toLowerCase().includes('pre-deploy')
    )
    .reduce<(typeof pipeline.steps)[0] | null>((latest, s) => {
      if (!latest) return s;
      return new Date(s.timestamp) > new Date(latest.timestamp) ? s : latest;
    }, null);
}

function validateConfig(text: string): string | null {
  if (!text.trim()) return null;
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed.actions)) return 'JSON must have an "actions" array';
    return null;
  } catch {
    return 'Invalid JSON';
  }
}

function validateHelmParams(text: string): string | null {
  if (!text.trim()) return null;
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return 'Must be a flat JSON object';
    for (const val of Object.values(parsed)) {
      if (typeof val === 'object' && val !== null) return 'Nested objects are not allowed — use flat key-value pairs';
    }
    return null;
  } catch {
    return 'Invalid JSON';
  }
}

function validateRbac(text: string): string | null {
  if (!text.trim()) return null;
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed.roles)) return 'JSON must have a "roles" array';
    return null;
  } catch {
    return 'Invalid JSON';
  }
}

const CONFIG_PLACEHOLDER = `// Paste your configuration.json here (optional)
// Example:
// {
//   "actions": [
//     {
//       "name": "Register API route",
//       "endpoint": "https://$eic_host/hub/apiprovisioning/v1/admin/v3/apis",
//       "method": "POST",
//       "data": { "serviceCapabilityId": "my-rapp-name" }
//     }
//   ]
// }`;

const HELM_PLACEHOLDER = `// Paste your userDefinedHelmParameters JSON here (optional)
// Example:
// {
//   "platformCaCertSecretName": "eic-tls-secret",
//   "appSecretName": "my-rapp-mtls-secret",
//   "platformCaCertFileName": "ca.crt",
//   "appCertFileName": "tls.crt",
//   "appKeyFileName": "tls.key",
//   "global.myCustomVar": "value"
// }`;

const RBAC_PLACEHOLDER = `// Paste your rbac-config.json here (optional)
// Example:
// {
//   "tenant": "master",
//   "roles": [
//     { "name": "MyApp_Application_Administrator", "attributes": {} },
//     { "name": "MyApp_ReadOnly", "attributes": {} }
//   ],
//   "authorization": { "resources": [] }
// }`;

export default function RappConfigDialog({ open, onClose, pipeline }: RappConfigDialogProps) {
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  const { theme } = useTheme();

  const manualStep = getManualConfigStep(pipeline);
  const stepMeta = (manualStep as any)?.metadata || {};
  const hasConfig: boolean = stepMeta.has_exposure_config === 'true';
  const manifest = stepMeta.manifest || '';
  const appname = stepMeta.appname || pipeline.name || '';
  const appversion = stepMeta.appversion || '';

  const [reapply, setReapply] = useState(false);
  const [configText, setConfigText] = useState('');
  const [helmText, setHelmText] = useState('');
  const [embeddedConfig, setEmbeddedConfig] = useState('');
  const [embeddedLoading, setEmbeddedLoading] = useState(false);
  const [rbacText, setRbacText] = useState('');

  const configError = validateConfig(configText);
  const helmError = validateHelmParams(helmText);
  const rbacError = validateRbac(rbacText);

  const flowId = pipeline.automationFlow.id;
  const ticketId = pipeline.TicketId;

  // Preview the CSAR-embedded configuration (saved to Minio during rapp-configuration)
  useEffect(() => {
    if (!open || !hasConfig) return;
    let cancelled = false;
    setEmbeddedLoading(true);
    rappConfigService
      .getConfig(flowId, ticketId)
      .then((resp) => {
        if (cancelled) return;
        const content = (resp as any)?.data?.content ?? '';
        let display = content;
        try {
          display = JSON.stringify(JSON.parse(content), null, 2);
        } catch {
          /* not JSON — show raw */
        }
        setEmbeddedConfig(display);
      })
      .catch(() => {
        if (!cancelled) setEmbeddedConfig('');
      })
      .finally(() => {
        if (!cancelled) setEmbeddedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, hasConfig, flowId, ticketId]);

  const handleClose = () => {
    setReapply(false);
    setConfigText('');
    setHelmText('');
    setRbacText('');
    onClose();
  };

  const handleBeforeNext = async (currentIdx: number) => {
    if (currentIdx === 1) {
      // step 1 → validate config if entered
      if (configError) return false;
    }
    return true;
  };

  const handleFinish = async () => {
    showLoader();
    try {
      if (configText.trim()) {
        await rappConfigService.saveConfig(flowId, ticketId, configText);
      }
      if (helmText.trim()) {
        await rappConfigService.saveHelmParams(flowId, ticketId, helmText);
      }
      if (rbacText.trim()) {
        await rappConfigService.saveRbac(flowId, ticketId, rbacText);
      }
      await rappConfigService.submit(flowId, ticketId, { manifest, appname, appversion });
      addNotifications([{
        title: 'Configuration submitted',
        icon: 'icon-check',
        description: `rApp deployment will continue for ${ticketId}`,
        color: 'green',
        timestamp: new Date()
      }]);
      handleClose();
    } catch (error) {
      addNotifications([{
        title: 'Submission failed',
        icon: 'icon-triangle-warning',
        description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred',
        color: 'red',
        timestamp: new Date()
      }]);
    } finally {
      hideLoader();
    }
  };

  if (!open) return null;

  const editorBg = theme ? '#FFFFFF' : '#181818';
  const readOnlyBg = theme ? '#F2F2F2' : '#383838';
  const editorColor = theme ? '#000000' : '#FFFFFF';

  // CSS targeting the actual <textarea> inside EDS wrapper — uses !important to override EDS styles
  const textAreaEditorStyle = `
    .rapp-editor textarea,
    .rapp-editor .eds-input__field,
    .rapp-editor [class*="input"] textarea,
    .rapp-editor [class*="textarea"] {
      background-color: ${editorBg} !important;
      color: ${editorColor} !important;
      font-family: monospace !important;
      font-size: 13px !important;
    }
    .rapp-readonly {
      background-color: ${readOnlyBg} !important;
      border-radius: 4px;
    }
  `;

  return ReactDOM.createPortal(
    <div className={theme ? 'light-theme' : 'dark-theme'} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
      <style>{textAreaEditorStyle}</style>
      <ReactEDSWrapper
        component={Tile}
        id={`rapp-config-dialog-${ticketId}`}
        title={`Configure rApp Deployment — ${ticketId}`}
        style={{ width: '80vw', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}
      >
        <ReactEDSWrapper
          component={Wizard}
          onCancel={handleClose}
          onFinish={handleFinish}
          onBeforeNext={handleBeforeNext}
          getCurrentStep={(_idx: number) => {}}
          cancelBtnLabel="Cancel"
          finishBtnLabel="Submit"
          nextBtnLabel="Next"
          previousBtnLabel="Back"
        >
          {/* Step 1: Service Exposure */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Service Exposure" valid={!configError}>
            <p style={{ marginBottom: '16px', color: '#8b8b8b' }}>
              {hasConfig
                ? '✅ Configuration from the CSAR package will be applied during deployment.'
                : '⚠️ No configuration.json found in the CSAR. You may provide one below (optional).'}
            </p>

            {hasConfig && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: 600, marginBottom: '6px' }}>Configuration from CSAR (will be applied at deployment):</p>
                <div className="rapp-editor">
                  <LineNumberedEditor
                    value={embeddedLoading ? 'Loading configuration…' : (embeddedConfig || '(embedded configuration not available)')}
                    readOnly
                    rows={12}
                    darkMode={!theme}
                  />
                </div>
              </div>
            )}

            {hasConfig && (
              <div style={{ marginBottom: '16px' }}>
                <ReactEDSWrapper
                  component={Checkbox}
                  id="reapply-checkbox"
                  checked={reapply}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setReapply(e.target.checked);
                    if (!e.target.checked) setConfigText('');
                  }}
                >
                  Reapply with a different configuration
                </ReactEDSWrapper>
              </div>
            )}

            {(!hasConfig || reapply) && (
              <>
                <div className="rapp-editor">
                  <LineNumberedEditor
                    value={configText}
                    onChange={setConfigText}
                    placeholder={CONFIG_PLACEHOLDER}
                    rows={12}
                    darkMode={!theme}
                  />
                </div>
                {configError && (
                  <p style={{ color: '#dc3545', marginTop: '6px', fontSize: '13px' }}>⚠️ {configError}</p>
                )}
              </>
            )}
          </ReactEDSWrapper>

          {/* Step 2: Helm Parameters */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Helm Parameters" valid={!helmError}>
            <p style={{ marginBottom: '16px', color: '#8b8b8b' }}>
              Provide runtime Helm values for this deployment (optional). These will be passed as <code>userDefinedHelmParameters</code>.
            </p>
            <div className="rapp-editor">
              <LineNumberedEditor
                value={helmText}
                onChange={setHelmText}
                placeholder={HELM_PLACEHOLDER}
                rows={12}
                darkMode={!theme}
              />
            </div>
            {helmError && (
              <p style={{ color: '#dc3545', marginTop: '6px', fontSize: '13px' }}>⚠️ {helmError}</p>
            )}
          </ReactEDSWrapper>

          {/* Step 3: RBAC Roles */}
          <ReactEDSWrapper component={WizardStep} stepTitle="RBAC Roles" valid={!rbacError}>
            <p style={{ marginBottom: '16px', color: '#8b8b8b' }}>
              Provide the RBAC roles/authorization for this rApp as <strong>JSON</strong> (optional). Paste the <code>rbac-config.json</code> content below — it will be applied to EIC IAM during deployment.
            </p>
            <div className="rapp-editor">
              <LineNumberedEditor
                value={rbacText}
                onChange={setRbacText}
                placeholder={RBAC_PLACEHOLDER}
                rows={12}
                darkMode={!theme}
              />
            </div>
            {rbacError && (
              <p style={{ color: '#dc3545', marginTop: '6px', fontSize: '13px' }}>⚠️ {rbacError}</p>
            )}
          </ReactEDSWrapper>

          {/* Step 4: Review */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Review & Submit" valid={true}>
            <p style={{ marginBottom: '16px', color: '#8b8b8b' }}>
              Review your inputs before submitting. Click Submit to resume the deployment.
            </p>
            <table className="rapp-readonly" style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '4px' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 600, padding: '8px', whiteSpace: 'nowrap', width: '200px' }}>Service Exposure Config</td>
                  <td style={{ padding: '8px', color: '#8b8b8b' }}>
                    {configText.trim() ? '✅ Provided' : hasConfig ? '✅ From CSAR (applied at deployment)' : '⬜ Skipped'}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '8px', whiteSpace: 'nowrap' }}>Helm Parameters</td>
                  <td style={{ padding: '8px', color: '#8b8b8b' }}>
                    {helmText.trim() ? `✅ ${helmError ? '(invalid JSON)' : Object.keys(JSON.parse(helmText)).length + ' parameter(s)'}` : '⬜ Skipped (defaults will be used)'}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '8px', whiteSpace: 'nowrap' }}>RBAC Roles</td>
                  <td style={{ padding: '8px', color: '#8b8b8b' }}>
                    {rbacText.trim() ? '✅ Provided' : '⬜ Skipped'}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '8px', whiteSpace: 'nowrap' }}>rApp</td>
                  <td style={{ padding: '8px' }}>{appname} {appversion}</td>
                </tr>
              </tbody>
            </table>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
      </ReactEDSWrapper>
    </div>,
    document.body
  );
}
